import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";

/**
 * Explicit height for a filled toggle group and the controls sharing its row (dropdowns, date
 * pickers). The selected-state rule below removes the button border, which would otherwise make
 * the active tab 2px shorter than its unselected neighbours and the row's other controls.
 */
export const FILLED_TOGGLE_CONTROL_HEIGHT = 40;

// Tab bar whose tabs are joined into one segmented control and whose selected tab is a filled
// primary pill instead of the theme's default light-blue one. Used by the Tauschbörse and Edikte
// source toggles and the Dienstplan (duty schedule) tab bars. Typed as the plain object form so a
// call site can merge extra rules into it via the sx array.
export const filledToggleGroupSx: SystemStyleObject<Theme> = {
  // The theme's MuiToggleButton root spaces every button with `marginRight` and gives each its own
  // `borderRadius`, which overrides the radii ToggleButtonGroup applies to join them. Undo both so
  // the tabs read as one segmented control: only the outer corners stay rounded.
  "& .MuiToggleButton-root": {
    // Set here rather than per call site: the selected-state rule below drops the button border, and
    // without an explicit height that alone makes the active tab 2px shorter than its neighbours.
    height: FILLED_TOGGLE_CONTROL_HEIGHT,
    marginRight: 0,
    "&:not(:last-of-type)": {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    "&:not(:first-of-type)": {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  "& .Mui-selected .MuiTypography-caption": {
    color: "inherit",
  },
  "& .MuiToggleButton-root.Mui-selected": {
    border: "none",
    backgroundColor: "primary.main",
    color: "primary.contrastText",
    "&:hover": {
      backgroundColor: "primary.dark",
      color: "primary.contrastText",
    },
  },
};
